import { Checkbox, DatePicker, Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { availabilityStoreHandler } from '../../pages/Timetable/Plan/TeachingBlockVersion/TeachingBlockVersion';
import {
  type TeachingBlockExclusionInput,
  TeachingBlockResourceType,
  useCreateTeachingBlockExclusionMutation,
  useUpdateTeachingBlockExclusionMutation,
} from '../../types/planung-graphql-client-defs';
import { isFirstAfterSecond, isFirstBeforeSecond } from '../../utils/dateCalculations';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import { TeachingBlockExclusionType } from '../TeachingBlockVersion/graphql/types';
import { useTeachingBlockVersionData } from '../TeachingBlockVersion/hooks/useTeachingBlockVersionData';

/** from/to sind DateTime-Scalars (Strings); im Formular wird mit echten Dates gearbeitet. */
type ExclusionFormValues = Omit<TeachingBlockExclusionType, 'from' | 'to' | 'holderType' | 'holderUuid'> & {
  from: Date;
  to: Date;
};

const HOLDER_TYPE = {
  Class: TeachingBlockResourceType.Class,
  Person: TeachingBlockResourceType.Teacher,
  Room: TeachingBlockResourceType.Room,
  Subject: TeachingBlockResourceType.Subject,
} as const;

export type ExclusionTimeframeFormProps = {
  type: 'Class' | 'Subject' | 'Person' | 'Room';
  teachingBlockVersionUuid: string;
  holderUuid: string;
  exclusionUuid?: string | null;
  onClose: () => void;
};

export const ExclusionTimeframeForm = ({
  type,
  teachingBlockVersionUuid,
  holderUuid,
  exclusionUuid,
  onClose,
}: ExclusionTimeframeFormProps) => {
  const { t } = useTranslation();

  const context = useMemorizedCacheTag('TEACHING_BLOCK_AVAILABILITY_EXCLUSION');

  const { versionData } = useTeachingBlockVersionData(teachingBlockVersionUuid);

  const data = versionData?.exclusions.find((e) => e.uuid === exclusionUuid);

  const values: ExclusionFormValues = {
    uuid: data?.uuid ?? '',
    from: data?.from ? new Date(data?.from) : new Date(),
    to: data?.to ? new Date(data?.to) : new Date(),
    priority: data?.priority ?? 0,
    comment: data?.comment ?? '',
  };

  const [, update] = useUpdateTeachingBlockExclusionMutation();
  const [, create] = useCreateTeachingBlockExclusionMutation();
  const availabilityStore = availabilityStoreHandler.get(teachingBlockVersionUuid);

  const toExclusionInput = (values: ExclusionFormValues): TeachingBlockExclusionInput => ({
    holderType: HOLDER_TYPE[type],
    holderUuid,
    from: values.from.toISOString(),
    to: values.to.toISOString(),
    priority: values.priority,
    comment: values.comment,
  });

  const handleSubmit = async (values: ExclusionFormValues, formHelpers: FormikHelpers<ExclusionFormValues>) => {
    if (values.uuid && data) {
      availabilityStore.removeExclusion({
        from: new Date(data.from),
        to: new Date(data.to),
        holder: {
          __typename: type,
          uuid: holderUuid,
        },
        priority: data.priority, // old value
      });

      availabilityStore.setExclusion({
        from: values.from,
        to: values.to,
        holder: {
          __typename: type,
          uuid: holderUuid,
        },
        priority: values.priority,
      });
      await update({ uuid: values.uuid, input: toExclusionInput(values) }, context);
    } else {
      availabilityStore.setExclusion({
        from: values.from,
        to: values.to,
        holder: {
          __typename: type,
          uuid: holderUuid,
        },
        priority: values.priority,
      });
      await create({ versionUuid: teachingBlockVersionUuid, input: toExclusionInput(values) }, context);
    }
    formHelpers.resetForm();
    onClose();
  };

  return (
    <Formik<ExclusionFormValues> onSubmit={handleSubmit} initialValues={values}>
      {({ values, errors, isSubmitting, isValidating, setFieldValue }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s' spacingTop='s' breakpoint='phone'>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('common.from')}
                    value={values.from}
                    onChange={async (date) => {
                      if (isFirstAfterSecond(date, values.to)) await setFieldValue('to', date);
                      await setFieldValue('from', date);
                    }}
                    name='from'
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePicker
                    label={t('common.until')}
                    value={values.to}
                    onChange={async (date) => {
                      if (isFirstBeforeSecond(date, values.from)) await setFieldValue('from', date);
                      await setFieldValue('to', date);
                    }}
                    name='to'
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s' spacingBottom='s'>
                <Input
                  label={t('common.comment')}
                  onChange={(event) => setFieldValue('comment', event.target.value)}
                  name='comment'
                  value={values.comment}
                />
              </GridRow>
              <GridRow spacingTop='none'>
                <Checkbox
                  name='is-partial'
                  onChange={(event) => setFieldValue('priority', event.target.checked ? 1 : 0)}
                  checked={values.priority === 1}
                  label={t('teachingBlockVersion.exclusion.partial')}
                />
              </GridRow>
            </Grid>
            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              errors={errors}
              isLoading={isSubmitting || isValidating}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
